/* QuestionsField — the survey question repeater, rebuilt on antd to `docs/design/survey-edit.html`.
 *
 * The old version was the last legacy holdout (`mp-form-*`, material icons, native selects). The
 * design is an ACCORDION: each question is a collapsible row that, closed, shows its number, text
 * and a strip of meta tags (required / answer-type-with-icon / priority / targeting summary) plus
 * move + delete controls; open, it reveals the editor — text, type, priority, required, dropdown
 * options (when the type is dropdown), and a collapsible Targeting sub-panel. One question is open
 * at a time.
 *
 * State: the question list is held locally and pushed up through `onChange` on every edit — the
 * position in the list IS the saved `order`. Targeting multi-selects store ids (the payload shape
 * the backend wants); `useResource`'s entity normalisation only walks top-level `descriptor.fields`,
 * not values nested in a `questions` array, so the id-unwrapping is done here.
 */
import React, { useState } from "react";
import { Button, Checkbox, Collapse, Empty, Flex, Input, Select, Tag, Typography } from "antd";
import {
  AlignLeftOutlined,
  CalendarOutlined,
  CameraOutlined,
  CheckSquareOutlined,
  DeleteOutlined,
  DownOutlined,
  FieldNumberOutlined,
  FilterOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  UpOutlined,
} from "@ant-design/icons";

import EntitySelect from "../../../component/EntitySelect";
import { POINTS_ADMIN } from "../../../config/lang";
import { translate } from "../../../util/util";
import {
  ANSWER_TYPES,
  ANSWER_TYPE_LABELS,
  PRIORITIES,
  PRIORITY_LABELS,
  PRODUCTS_SOURCES,
  PRODUCTS_SOURCE_LABELS,
  QUESTION_MODES,
  QUESTION_MODE_LABELS,
  TARGETING_GROUPS,
} from "./questionOptions";

/** Answer-type glyphs (the mockup's `A_TYPE_ICON`, mapped to verified antd icons). */
const ANSWER_TYPE_ICON = {
  numeric: FieldNumberOutlined,
  text: AlignLeftOutlined,
  photo: CameraOutlined,
  dropdown: UnorderedListOutlined,
  date: CalendarOutlined,
  boolean: CheckSquareOutlined,
};

const emptyQuestion = () => ({
  text: "",
  answer_type: "text",
  required: false,
  priority: 0,
  question_mode: null,
  products_source: null,
  all_products: false,
  all_brands: false,
  all_categories: false,
  all_subcategories: false,
  products: [],
  brands: [],
  categories: [],
  subcategories: [],
  dropdown_options: [],
});

const emptyOption = () => ({ label: "", value: "" });

// EntitySelect reports selections as option OBJECTS; the backend M2M fields want ids. This repeater
// is not covered by useResource's top-level normalisation, so ids are taken here.
const toIds = list => (list || []).map(x => (x && typeof x === "object" ? x.id ?? x.value ?? x : x));

// The list position IS the saved order; reordering re-derives it.
const withOrder = list => list.map((row, i) => ({ ...row, order: i }));

export default function QuestionsField({ field, value, onChange, lang }) {
  const t = key => translate(POINTS_ADMIN, key, lang);
  const [questions, setQuestions] = useState(() => (value || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
  const [active, setActive] = useState([]); // the open row(s) — accordion keeps this to one
  const [targetingOpen, setTargetingOpen] = useState({});

  const sync = next => {
    setQuestions(next);
    onChange(field.name, withOrder(next));
  };

  const updateQuestion = (qi, patch) => sync(questions.map((q, idx) => (idx === qi ? { ...q, ...patch } : q)));

  const addQuestion = () => {
    const next = [...questions, emptyQuestion()];
    sync(next);
    setActive([String(next.length - 1)]); // open the new one
  };

  const removeQuestion = qi => {
    sync(questions.filter((_, idx) => idx !== qi));
    setActive([]);
  };

  const moveQuestion = (qi, dir) => {
    const j = qi + dir;
    if (j < 0 || j >= questions.length) return;
    const next = questions.slice();
    [next[qi], next[j]] = [next[j], next[qi]];
    sync(next);
  };

  const updateOptions = (qi, options) => updateQuestion(qi, { dropdown_options: withOrder(options) });
  const addOption = qi => updateOptions(qi, [...(questions[qi].dropdown_options || []), emptyOption()]);
  const removeOption = (qi, oi) =>
    updateOptions(
      qi,
      questions[qi].dropdown_options.filter((_, idx) => idx !== oi)
    );
  const updateOption = (qi, oi, patch) =>
    updateOptions(
      qi,
      questions[qi].dropdown_options.map((o, idx) => (idx === oi ? { ...o, ...patch } : o))
    );

  /** "All products · 3 brands" — the one-line summary shown on the closed row and the targeting bar. */
  const targetSummary = q => {
    const parts = [];
    TARGETING_GROUPS.forEach(g => {
      if (q[g.allKey]) parts.push(t(g.allLabelKey));
      else if ((q[g.key] || []).length) parts.push(`${q[g.key].length} ${t(g.labelKey).toLowerCase()}`);
    });
    return parts.length ? parts.join(" · ") : null;
  };

  const stop = e => e.stopPropagation();

  const rowHeader = (q, qi) => {
    const Icon = ANSWER_TYPE_ICON[q.answer_type] || AlignLeftOutlined;
    const summary = targetSummary(q);
    const hasText = q.text.trim().length > 0;
    return (
      <Flex align="center" gap={10} className="pt-q__head">
        <span className="pt-q__n">{qi + 1}</span>
        <Typography.Text strong ellipsis className={hasText ? "pt-q__t" : "pt-q__t pt-q__t--empty"}>
          {hasText ? q.text : t("f_question_text")}
        </Typography.Text>
        <Flex className="pt-q__meta" align="center" gap={6}>
          {q.required && <Tag>{t("f_required")}</Tag>}
          <Tag icon={<Icon />}>{t(ANSWER_TYPE_LABELS[q.answer_type])}</Tag>
          <Tag>{t(PRIORITY_LABELS[q.priority])}</Tag>
          {summary && (
            <Tag icon={<FilterOutlined />} className="pt-q__tgt" title={`${t("sec_targeting")}: ${summary}`}>
              {summary}
            </Tag>
          )}
        </Flex>
      </Flex>
    );
  };

  const rowExtra = qi => (
    <Flex align="center" gap={2} className="pt-q__acts" onClick={stop}>
      <Button
        type="text"
        size="small"
        icon={<UpOutlined />}
        disabled={qi === 0}
        aria-label={t("question_move_up")}
        title={t("question_move_up")}
        onClick={e => {
          stop(e);
          moveQuestion(qi, -1);
        }}
      />
      <Button
        type="text"
        size="small"
        icon={<DownOutlined />}
        disabled={qi === questions.length - 1}
        aria-label={t("question_move_down")}
        title={t("question_move_down")}
        onClick={e => {
          stop(e);
          moveQuestion(qi, 1);
        }}
      />
      <Button
        type="text"
        size="small"
        danger
        icon={<DeleteOutlined />}
        aria-label={t("question_remove_btn")}
        title={t("question_remove_btn")}
        onClick={e => {
          stop(e);
          removeQuestion(qi);
        }}
      />
    </Flex>
  );

  const questionBody = (q, qi) => {
    const summary = targetSummary(q);
    return (
      <Flex vertical gap="middle" className="pt-q__body">
        <div className="pt-qform">
          <label className="pt-qf pt-qf--wide">
            <span className="pt-qf__label">
              {t("f_question_text")}
              <span className="pt-req"> *</span>
            </span>
            <Input value={q.text} placeholder={t("f_question_text")} onChange={e => updateQuestion(qi, { text: e.target.value })} />
          </label>
          <label className="pt-qf">
            <span className="pt-qf__label">{t("f_answer_type")}</span>
            <Select
              value={q.answer_type}
              onChange={v => updateQuestion(qi, { answer_type: v })}
              options={ANSWER_TYPES.map(v => ({ value: v, label: t(ANSWER_TYPE_LABELS[v]) }))}
            />
          </label>
          <label className="pt-qf">
            <span className="pt-qf__label">{t("f_priority")}</span>
            <Select
              value={q.priority}
              onChange={v => updateQuestion(qi, { priority: v })}
              options={PRIORITIES.map(v => ({ value: v, label: t(PRIORITY_LABELS[v]) }))}
            />
          </label>
          <div className="pt-qf pt-qf--check">
            <Checkbox checked={!!q.required} onChange={e => updateQuestion(qi, { required: e.target.checked })}>
              {t("f_required")}
            </Checkbox>
          </div>
        </div>

        {q.answer_type === "dropdown" && (
          <div className="pt-subsec">
            <span className="pt-subsec__t">
              <UnorderedListOutlined /> {t("f_dropdown_options")}
            </span>
            <Flex vertical gap={6} className="pt-optlist">
              {(q.dropdown_options || []).map((opt, oi) => (
                <div className="pt-optrow" key={oi}>
                  <Input value={opt.label} placeholder={t("option_label_ph")} onChange={e => updateOption(qi, oi, { label: e.target.value })} />
                  <Input value={opt.value} placeholder={t("option_value_ph")} onChange={e => updateOption(qi, oi, { value: e.target.value })} />
                  <Button type="text" icon={<DeleteOutlined />} aria-label={t("option_remove")} onClick={() => removeOption(qi, oi)} />
                </div>
              ))}
              <div>
                <Button size="small" icon={<PlusOutlined />} onClick={() => addOption(qi)}>
                  {t("option_add_btn")}
                </Button>
              </div>
            </Flex>
          </div>
        )}

        {/* Targeting: collapsed by default, its summary shown on the toggle. */}
        <div className="pt-subsec">
          <button
            type="button"
            className="pt-subsec__toggle"
            aria-expanded={!!targetingOpen[qi]}
            onClick={() => setTargetingOpen(s => ({ ...s, [qi]: !s[qi] }))}>
            <DownOutlined className={`pt-subsec__chev${targetingOpen[qi] ? " pt-subsec__chev--open" : ""}`} />
            <span>{t("sec_targeting")}</span>
            <span className="pt-subsec__sum">{summary || t("all_answers")}</span>
          </button>

          {targetingOpen[qi] && (
            <Flex vertical gap="middle" className="pt-q__targeting">
              <div className="pt-qform">
                <label className="pt-qf">
                  <span className="pt-qf__label">{t("f_question_mode")}</span>
                  <Select
                    allowClear
                    value={q.question_mode || undefined}
                    placeholder="—"
                    onChange={v => updateQuestion(qi, { question_mode: v || null })}
                    options={QUESTION_MODES.map(v => ({ value: v, label: t(QUESTION_MODE_LABELS[v]) }))}
                  />
                </label>
                <label className="pt-qf">
                  <span className="pt-qf__label">{t("f_products_source")}</span>
                  <Select
                    allowClear
                    value={q.products_source || undefined}
                    placeholder="—"
                    onChange={v => updateQuestion(qi, { products_source: v || null })}
                    options={PRODUCTS_SOURCES.map(v => ({ value: v, label: t(PRODUCTS_SOURCE_LABELS[v]) }))}
                  />
                </label>
              </div>

              <Flex vertical gap="small">
                {TARGETING_GROUPS.map(g => (
                  <div className="pt-tgroup__r" key={g.key}>
                    <Checkbox checked={!!q[g.allKey]} onChange={e => updateQuestion(qi, { [g.allKey]: e.target.checked })}>
                      {t(g.allLabelKey)}
                    </Checkbox>
                    <label className="pt-qf">
                      <span className="pt-qf__label">{t(g.labelKey)}</span>
                      {/* Kept mounted and disabled when "All" is on — unmounting flickered and wiped
                          the selection. */}
                      <EntitySelect
                        entity={g.entity}
                        value={q[g.key] || []}
                        isMulti
                        tall
                        disabled={!!q[g.allKey]}
                        extraQuery={{ active: true }}
                        ariaLabel={t(g.labelKey)}
                        onChange={o => updateQuestion(qi, { [g.key]: toIds(o) })}
                      />
                    </label>
                  </div>
                ))}
              </Flex>
            </Flex>
          )}
        </div>
      </Flex>
    );
  };

  const items = questions.map((q, qi) => ({
    key: String(qi),
    label: rowHeader(q, qi),
    extra: rowExtra(qi),
    children: questionBody(q, qi),
  }));

  return (
    <Flex vertical gap="middle">
      {questions.length ? (
        <Collapse
          accordion
          activeKey={active}
          onChange={keys => setActive(keys)}
          /* The header and chevron toggle; the move/delete controls in `extra` stop propagation
             (see `rowExtra`), so they act without opening/closing the panel. */
          items={items}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("no_questions")} />
      )}
      <div>
        <Button icon={<PlusOutlined />} onClick={addQuestion}>
          {t("question_add_btn")}
        </Button>
      </div>
    </Flex>
  );
}
